import { useCallback, useRef, useState } from 'react'
import { Eye, Film, Play, Tag } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { StoryboardFrameData } from './types'
import { CONTENT_LABEL_COLORS, CONTENT_LABELS } from './types'

function getContentLabelStyle(label: string): { bg: string; text: string } {
  const match = CONTENT_LABELS.find(l => l.toLowerCase() === label.toLowerCase())
  if (match) return CONTENT_LABEL_COLORS[match]
  return CONTENT_LABEL_COLORS.Scene
}

function MediaCell({
  frame,
  showVideo,
  onClick,
}: {
  frame: StoryboardFrameData
  showVideo: boolean
  onClick?: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [imageError, setImageError] = useState(false)

  const hasImage = frame.image && frame.image.length > 0 && !imageError
  const hasVideo = frame.videoUrl && frame.videoUrl.length > 0

  const handleMouseEnter = useCallback(() => {
    if (!hasVideo || !showVideo) return
    setIsHovering(true)
    videoRef.current?.play().catch(() => {})
  }, [hasVideo, showVideo])

  const handleMouseLeave = useCallback(() => {
    if (!hasVideo || !showVideo) return
    setIsHovering(false)
    const vid = videoRef.current
    if (vid) {
      vid.pause()
      vid.currentTime = 0
    }
  }, [hasVideo, showVideo])

  const isClickable = onClick && (hasImage || hasVideo)

  return (
    <div
      className={cn(
        'relative aspect-[4/5] w-full rounded-md overflow-hidden bg-gray-50',
        isClickable && 'cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={isClickable ? onClick : undefined}
    >
      {showVideo && hasVideo ? (
        <>
          <video
            ref={videoRef}
            src={frame.videoUrl}
            poster={hasImage ? frame.image : undefined}
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
          {!isHovering && (
            <div className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
              <Play className="h-2.5 w-2.5 text-white fill-white ml-0.5" />
            </div>
          )}
        </>
      ) : hasImage ? (
        <img
          src={frame.image}
          alt={`Scene ${frame.timeStart}`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 border border-dashed border-gray-200 rounded-md">
          <Film className="h-5 w-5 text-gray-300" />
        </div>
      )}
    </div>
  )
}

interface SourceVideoTableProps {
  frames: StoryboardFrameData[]
  className?: string
  onFrameClick?: (frame: StoryboardFrameData) => void
}

export function SourceVideoTable({ frames, className, onFrameClick }: SourceVideoTableProps) {
  const [showTags, setShowTags] = useState(false)
  const [showVideo, setShowVideo] = useState(false)

  if (frames.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Film className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500">No source frames</p>
        <p className="text-xs text-gray-400 mt-1">Upload a video to analyze its scenes</p>
      </div>
    )
  }

  return (
    <div className={cn('', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Source Video</h3>
          <button
            onClick={() => setShowTags(!showTags)}
            className={cn(
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors',
              showTags
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            <Tag className="h-3 w-3" />
            Show tags
          </button>
        </div>
        <button
          onClick={() => setShowVideo(!showVideo)}
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
            showVideo
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          )}
        >
          <Eye className="h-3 w-3" />
          Show Video
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <tbody>
            {/* Content Row */}
            <tr className="border-b border-gray-100">
              <td className="py-2 pr-4 align-top w-24 flex-shrink-0">
                <span className="text-xs font-medium text-gray-500">Content</span>
              </td>
              {frames.map(frame => {
                const style = getContentLabelStyle(frame.label)
                return (
                  <td key={`content-${frame.id}`} className="py-2 px-2 align-top">
                    <span
                      className={cn(
                        'inline-block px-2.5 py-1 rounded text-xs font-semibold',
                        style.bg,
                        style.text
                      )}
                    >
                      {frame.label}
                    </span>
                    {showTags && frame.visualTreatment && (
                      <div className="mt-1.5">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500">
                          {frame.visualTreatment.replace(/_/g, ' ')}
                        </span>
                      </div>
                    )}
                  </td>
                )
              })}
            </tr>

            {/* Time Row */}
            <tr className="border-b border-gray-100">
              <td className="py-2 pr-4 align-middle w-24">
                <span className="text-xs font-medium text-gray-500">Time</span>
              </td>
              {frames.map(frame => (
                <td key={`time-${frame.id}`} className="py-2 px-2 align-middle">
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    {frame.timeStart} → {frame.timeEnd}
                  </span>
                </td>
              ))}
            </tr>

            {/* Media Row */}
            <tr className="border-b border-gray-100">
              <td className="py-3 pr-4 align-middle w-24">
                <span className="text-xs font-medium text-gray-500">Media</span>
              </td>
              {frames.map(frame => (
                <td key={`media-${frame.id}`} className="py-3 px-2 align-top">
                  <div className="w-[140px]">
                    <MediaCell
                      frame={frame}
                      showVideo={showVideo}
                      onClick={onFrameClick ? () => onFrameClick(frame) : undefined}
                    />
                  </div>
                </td>
              ))}
            </tr>

            {/* Text Overlay Row */}
            <tr className="border-b border-gray-100">
              <td className="py-2 pr-4 align-top w-24">
                <span className="text-xs font-medium text-gray-500">Text overlay</span>
              </td>
              {frames.map(frame => (
                <td key={`text-${frame.id}`} className="py-2 px-2 align-top">
                  <p className="text-xs text-gray-700 leading-relaxed max-w-[160px]">
                    {frame.textOverlay || '—'}
                  </p>
                </td>
              ))}
            </tr>

            {/* Audio Row */}
            <tr>
              <td className="py-2 pr-4 align-top w-24">
                <span className="text-xs font-medium text-gray-500">Audio</span>
              </td>
              {frames.map(frame => (
                <td key={`audio-${frame.id}`} className="py-2 px-2 align-top">
                  <p className="text-xs text-gray-600 italic leading-relaxed max-w-[160px]">
                    {frame.audioText || '—'}
                  </p>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
